import { useNavigate } from "react-router-dom";
import { CalendarDays, HardHat, Wallet, ReceiptEuro, Mail, ChevronRight, AlertTriangle } from "lucide-react";
import { fmtDay } from "@/lib/cantieri";
import { useAuth } from "@/context/AuthContext";
import { isSimple } from "@/lib/edition";

const Box = ({ title, icon: Icon, color, to, count, children, testid }) => {
  const navigate = useNavigate();
  return (
    <section className="min-w-0 rounded-2xl border border-border bg-card overflow-hidden animate-in-up" data-testid={testid}>
      <button type="button" onClick={() => navigate(to)} className="w-full flex items-center gap-3 px-4 py-3 border-b border-border text-left hover:bg-secondary/50 transition-colors">
        <span className="w-9 h-9 rounded-lg text-white flex items-center justify-center shrink-0" style={{ background: color }}><Icon className="w-[18px] h-[18px]" /></span>
        <span className="font-heading font-semibold flex-1">{title}</span>
        <span className="text-sm font-bold tabular-nums" data-testid={`${testid}-count`}>{count}</span>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </button>
      <div className="px-4 py-3 space-y-2.5 text-sm" data-no-translate>{children}</div>
    </section>
  );
};

const Row = ({ main, sub, right, alert }) => (
  <div className="flex items-start gap-3">
    <div className="flex-1 min-w-0">
      <div className={`font-medium truncate ${alert ? "text-[#DC2626]" : ""}`}>{main}</div>
      {sub && <div className="text-xs text-muted-foreground truncate">{sub}</div>}
    </div>
    {right && <div className="text-xs font-semibold shrink-0 tabular-nums">{right}</div>}
  </div>
);

const Empty = ({ children }) => <div className="text-muted-foreground">{children}</div>;
const Label = ({ children }) => <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground pt-1">{children}</div>;
const join = (...a) => a.filter(Boolean).join(" · ");

const Mailbox = ({ em, wa }) => {
  const navigate = useNavigate();
  if (!em) return <Empty>Casella email non collegata. <button type="button" className="text-primary font-medium underline" onClick={() => navigate("/impostazioni")} data-testid="today-connect-mail">Collegala</button> per vedere le email nel report.</Empty>;
  if (em.errore) return <Empty>{em.errore}.</Empty>;
  return (
    <>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Arrivate (24 ore)", em.ultime_24h, "today-mail-24h"], ["Non lette", em.non_lette, "today-mail-unread"], ["WhatsApp", wa || 0, "today-wa-unread"]].map(([l, v, id]) => (
          <div key={l} className="rounded-lg bg-secondary/60 py-2"><div className="font-heading text-lg font-bold" data-testid={id}>{v}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>
        ))}
      </div>
      <Label>Possibili pagamenti arrivati via email</Label>
      {em.possibili_pagamenti?.length ? em.possibili_pagamenti.map((p) => <Row key={p.uid} main={p.oggetto} sub={join(p.da, p.data)} alert />)
        : <Empty>Nessuna email con fatture, bollette o avvisi di pagamento.</Empty>}
    </>
  );
};

// Sezioni del report giornaliero, colorate come l'agenda (blu, arancio, rosso).
export const ReportSections = ({ d }) => {
  const { user } = useAuth();
  const simple = isSimple(user);
  const pays = [...d.pagamenti_scaduti.map((p) => ({ ...p, k: "s" })), ...d.pagamenti_oggi.map((p) => ({ ...p, k: "o" })), ...d.pagamenti_domani.map((p) => ({ ...p, k: "d" }))];
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="today-sections">
      <Box title="Appuntamenti di oggi" icon={CalendarDays} color="#0284C7" to="/agenda" count={d.appuntamenti.length} testid="today-appointments">
        {d.appuntamenti.length ? d.appuntamenti.map((a, i) => <Row key={i} main={`${a.ora} · ${a.titolo}`} sub={join(a.cliente, a.luogo, a.assegnato_a)} />) : <Empty>Nessun appuntamento oggi.</Empty>}
      </Box>
      {!simple && (
      <Box title="Cantieri" icon={HardHat} color="#EA580C" to="/cantieri" count={d.cantieri_attivi.length} testid="today-sites">
        {d.cantieri_attivi.length ? d.cantieri_attivi.map((c, i) => <Row key={i} main={c.nome} sub={join(c.cliente, c.indirizzo, `${c.operai} ${c.operai === 1 ? "operaio" : "operai"}`)} right={c.fine ? `fino al ${fmtDay(c.fine)}` : ""} />) : <Empty>Nessun cantiere attivo oggi.</Empty>}
        {d.cantieri_domani.length > 0 && <><Label>In partenza domani</Label>{d.cantieri_domani.map((n) => <Row key={n} main={n} />)}</>}
      </Box>
      )}
      <Box title="Pagamenti" icon={Wallet} color="#DC2626" to={simple ? "/agenda" : "/pagamenti-incassi"} count={pays.length} testid="today-payments">
        {pays.length ? pays.map((p, i) => <Row key={i} main={p.titolo} alert={p.k === "s"} right={p.importo} sub={p.k === "s" ? `Scaduto il ${fmtDay(p.data)}` : p.k === "o" ? "Oggi" : "Domani"} />) : <Empty>Nessun pagamento in scadenza.</Empty>}
      </Box>
      {!simple && (
      <Box title="Incassi e preventivi" icon={ReceiptEuro} color="#4F46E5" to="/preventivi" count={d.rate_da_incassare.length + d.preventivi_in_scadenza.length} testid="today-income">
        {d.rate_da_incassare.map((r, i) => <Row key={`r${i}`} main={`Fattura ${r.fattura} · ${r.cliente}`} sub={`Rata scaduta il ${fmtDay(r.scadenza)}`} right={r.importo} alert />)}
        {d.preventivi_in_scadenza.map((q, i) => <Row key={`q${i}`} main={`Preventivo N. ${q.numero}`} sub={q.cliente} right={`scade ${fmtDay(q.scadenza)}`} />)}
        {!d.rate_da_incassare.length && !d.preventivi_in_scadenza.length && <Empty>Nessuna rata da incassare e nessun preventivo in scadenza.</Empty>}
      </Box>
      )}
      <div className="md:col-span-2 min-w-0">
        <Box title="Posta e WhatsApp" icon={Mail} color="#0D9488" to="/posta" count={d.email?.ultime_24h ?? "—"} testid="today-mail">
          <Mailbox em={d.email} wa={d.whatsapp_non_letti} />
          {d.email?.possibili_pagamenti?.length > 0 && <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1"><AlertTriangle className="w-3.5 h-3.5" /> Controlla le email segnalate: potrebbero contenere importi da pagare.</div>}
        </Box>
      </div>
    </div>
  );
};

export default ReportSections;
